/* eslint-disable max-lines */
import type { FC } from "react"

import { useTranslation } from "react-i18next"
// eslint-disable-next-line no-restricted-imports -- pre-existing Recoil selector, retires with the Proposal-state migration
import { selector, useRecoilState } from "recoil"
import styled from "styled-components"

import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import { useUtilityActions } from "~/containers/Proposal/state/utility-rate/actions"
import {
  allUtilityRatesState,
  selectedAfterSolarCompanyNameState,
  selectedAfterSolarRatePlanState,
  selectedBeforeSolarCompanyNameState,
  selectedBeforeSolarRatePlanState,
} from "~/containers/Proposal/state/utility-rate/utility-rate-state"
import { Dropdown } from "~/ui/lib/options/Select/Dropdown"

import { ReactComponent as ResetIcon } from "@svg/reset.svg"

import { NumberInput, NumberInputWrapper } from "./ui"
import { UtilityRateWrapper } from "./UtilityRateInput"

const EscWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  max-width: 65px;
  label {
    font-size: 18px;
    font-weight: 500;
    text-transform: capitalize;
    white-space: nowrap;
    color: var(--queen-blue);
  }
  gap: 7px;
  label {
    font-size: 12px;
    font-weight: 600;
  }
  input {
    font-size: 16px;
    font-weight: 500;
    height: 50px;
    border: 1px solid #c4c4c4;
    border-radius: 6px;
    padding: 0 5px 0 0;
    text-align: right;
    max-width: 60px;
  }
`

export const BeforeSolarCompanySelect: FC = () => {
  const { t } = useTranslation()

  const { handleCompanyNameChange, beforeSolarCompanyNameOptions } =
    useUtilityActions()
  const [selectedBeforeSolarCompanyName, setSelectedBeforeSolarCompanyName] =
    useRecoilState(selectedBeforeSolarCompanyNameState)

  return (
    <Dropdown.Container
      Label={() => <label>{t(`utilityCompany`)}</label>}
      SelectedOption={() => (
        <p>{selectedBeforeSolarCompanyName[0] ?? `Select...`}</p>
      )}
      Options={beforeSolarCompanyNameOptions.map((option) => (
        <Dropdown.Item
          key={option}
          label1={option}
          isChecked={selectedBeforeSolarCompanyName[0] === option}
          onClick={() => (
            setSelectedBeforeSolarCompanyName([option]),
            handleCompanyNameChange([option], `current`)
          )}
        />
      ))}
    />
  )
}

export const BeforeSolarRateSelect: FC = () => {
  const { t } = useTranslation()

  const { handleRatePlanChange, beforeSolarRatePlanOptions } =
    useUtilityActions()
  const [selectedBeforeSolarRatePlan, setSelectedBeforeSolarRatePlan] =
    useRecoilState(selectedBeforeSolarRatePlanState)

  return (
    <Dropdown.Container
      Label={() => <label>{t(`ratePlan`)}</label>}
      SelectedOption={() => (
        <p>{selectedBeforeSolarRatePlan?.tariffName ?? `Select...`}</p>
      )}
      Options={beforeSolarRatePlanOptions.map((option) => (
        <Dropdown.Item
          key={`${option.tariffId}-${option.tariffName}`}
          label1={option.tariffName}
          isChecked={selectedBeforeSolarRatePlan?.id === option.id}
          onClick={() => (
            setSelectedBeforeSolarRatePlan(option),
            handleRatePlanChange(option, `current`)
          )}
        />
      ))}
    />
  )
}

export const AfterSolarCompanySelect: FC = () => {
  const { t } = useTranslation()
  const { handleCompanyNameChange, afterSolarCompanyNameOptions } =
    useUtilityActions()
  const [selectedAfterSolarCompanyName, setSelectedAfterSolarCompanyName] =
    useRecoilState(selectedAfterSolarCompanyNameState)

  return (
    <Dropdown.Container
      Label={() => <label>{t(`utilityCompany`)}</label>}
      SelectedOption={() => (
        <p>{selectedAfterSolarCompanyName[0] ?? `Select...`}</p>
      )}
      Options={afterSolarCompanyNameOptions.map((option) => (
        <Dropdown.Item
          key={option}
          label1={option}
          isChecked={selectedAfterSolarCompanyName[0] === option}
          onClick={() => (
            setSelectedAfterSolarCompanyName([option]),
            handleCompanyNameChange([option], `post-solar`)
          )}
        />
      ))}
    />
  )
}

export const AfterSolarRateSelect: FC = () => {
  const { t } = useTranslation()
  const { handleRatePlanChange, afterSolarRatePlanOptions } = useUtilityActions()

  const [selectedAfterSolarRatePlan, setSelectedAfterSolarRatePlan] =
    useRecoilState(selectedAfterSolarRatePlanState)

  return (
    <Dropdown.Container
      Label={() => <label>{t(`ratePlan`)}</label>}
      SelectedOption={() => (
        <p>{selectedAfterSolarRatePlan?.tariffName ?? `Select...`}</p>
      )}
      Options={afterSolarRatePlanOptions.map((option) => (
        <Dropdown.Item
          key={`${option.tariffId}-${option.tariffName}`}
          label1={option.tariffName}
          isChecked={selectedAfterSolarRatePlan?.id === option.id}
          onClick={() => (
            setSelectedAfterSolarRatePlan(option),
            handleRatePlanChange(option, `post-solar`)
          )}
        />
      ))}
    />
  )
}

export const EscNumberInput: FC = () => {
  const { t } = useTranslation()
  const proposal = useProposalContext()

  const escPercentageChange = (value: number) => {
    if (value > 6) value = 6
    if (value < 0) value = 0

    proposal.changeLocal({
      utilityRateEscalationPercentage: Math.trunc(value),
    })
  }

  return (
    <EscWrapper>
      <label htmlFor="escalation">{t(`escalationPercent`)}</label>
      <input
        id="escalation"
        type="number"
        value={proposal.local.utilityRateEscalationPercentage ?? 0}
        onChange={(e) => escPercentageChange(Number(e.target.value))}
        min="0"
        max="6"
        step="1"
      />
    </EscWrapper>
  )
}

export const AfterSolarContractedRateInput: FC = () => {
  const { t } = useTranslation()
  const proposal = useProposalContext()

  const afterSolarContractedRateChange = (value: number | null) => {
    if (typeof value === `number`) {
      if (value <= 0) value = null
    }

    proposal.changeLocal({
      userAdjustedAfterSolarContractedRate: value,
    })
  }

  const isUserAdjusted =
    proposal.local.userAdjustedAfterSolarContractedRate !== null

  return (
    <UtilityRateWrapper>
      <NumberInputWrapper>
        <div data-role="label-wrapper">
          <label
            htmlFor="afterSolarContractedRate"
            style={{ textTransform: `none` }}
          >
            {t(`contractedRate`)}
          </label>
          {isUserAdjusted ? (
            <button
              title="reset"
              onClick={() => {
                proposal.changeLocal({
                  userAdjustedAfterSolarContractedRate: null,
                })
              }}
            >
              <ResetIcon />
            </button>
          ) : null}
        </div>
        <NumberInput
          placeholder={`n/a`}
          id="afterSolarContractedRate"
          value={proposal.local.userAdjustedAfterSolarContractedRate}
          defaultValue={proposal.local.genabilityDefaultContractedRate?.toFixed(
            2,
          )}
          setValue={(v) => {
            afterSolarContractedRateChange(v ?? null)
          }}
          decimalPlaces={2}
          decimalPlacesDisplayed={2}
          min={0}
          testId="after-solar-contracted-rate"
        />
      </NumberInputWrapper>
    </UtilityRateWrapper>
  )
}

export const BeforeSolarContractedRateInput: FC = () => {
  const { t } = useTranslation()
  const proposal = useProposalContext()

  const beforeSolarContractedRateChange = (value: number | null) => {
    if (typeof value === `number`) {
      if (value <= 0) value = null
    }

    proposal.changeLocal({
      userAdjustedBeforeSolarContractedRate: value,
      userAdjustedUtilityRate: null,
    })
  }

  const isUserAdjusted =
    proposal.local.userAdjustedBeforeSolarContractedRate !== null

  return (
    <UtilityRateWrapper>
      <NumberInputWrapper>
        <div data-role="label-wrapper">
          <label
            htmlFor="beforeSolarContractedRate"
            style={{ textTransform: `none` }}
          >
            {t(`contractedRate`)}
          </label>
          {isUserAdjusted ? (
            <button
              title="reset"
              onClick={() => {
                proposal.changeLocal({
                  userAdjustedBeforeSolarContractedRate: null,
                  userAdjustedUtilityRate: null,
                })
              }}
            >
              <ResetIcon />
            </button>
          ) : null}
        </div>
        <NumberInput
          id="beforeSolarContractedRate"
          value={proposal.local.userAdjustedBeforeSolarContractedRate}
          defaultValue={proposal.local.genabilityDefaultContractedRate?.toFixed(
            2,
          )}
          setValue={(v) => {
            beforeSolarContractedRateChange(v ?? null)
          }}
          placeholder={`n/a`}
          decimalPlaces={2}
          decimalPlacesDisplayed={2}
          min={0}
          testId="before-solar-contracted-rate"
        />
      </NumberInputWrapper>
    </UtilityRateWrapper>
  )
}

export const beforeSolarPlanHasContractedRatesState = selector<boolean>({
  key: `beforeSolarPlanHasContractedRates`,
  get: ({ get }) => {
    const selectedPreSolarRate = get(selectedBeforeSolarRatePlanState)
    const selectedPreSolarCompany = get(selectedBeforeSolarCompanyNameState)
    const rates = get(allUtilityRatesState)
    const selectedPreSolarHasContractedRates = rates.find(
      (rate) =>
        rate.lseName === selectedPreSolarCompany[0] &&
        rate.tariffName === selectedPreSolarRate?.tariffName,
    )?.hasContractedRates

    return !!selectedPreSolarHasContractedRates
  },
})

export const afterSolarPlanHasContractedRatesState = selector<boolean>({
  key: `afterSolarPlanHasContractedRates`,
  get: ({ get }) => {
    const selectedAfterSolarRate = get(selectedAfterSolarRatePlanState)
    const selectedAfterSolarCompany = get(selectedAfterSolarCompanyNameState)
    const rates = get(allUtilityRatesState)
    const selectedAfterSolarHasContractedRates = rates.find(
      (rate) =>
        rate.lseName === selectedAfterSolarCompany[0] &&
        rate.tariffName === selectedAfterSolarRate?.tariffName,
    )?.hasContractedRates

    return !!selectedAfterSolarHasContractedRates
  },
})
